import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import {
  Users, FileText, Star, ShieldCheck, Trophy, Buildings, TrendUp,
  MapPin, ChartBar, Clock, ArrowRight, Warning
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BAR_COLORS = [
  "bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-purple-500", "bg-sky-500",
  "bg-rose-500", "bg-indigo-500", "bg-teal-500", "bg-orange-500", "bg-cyan-500",
  "bg-lime-500", "bg-fuchsia-500", "bg-violet-500", "bg-pink-500", "bg-yellow-500",
];

export default function SupplierAnalyticsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await API.get("/suppliers/analytics-overview");
        setData(d);
      } catch { toast.error("Failed to load analytics"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!data) return null;

  const { summary, status_distribution, sector_distribution, area_distribution, rating_breakdown, top_rated } = data;
  const maxSectorCount = sector_distribution.length ? Math.max(...sector_distribution.map(s => s.count)) : 1;
  const maxAreaCount = (area_distribution || []).length ? Math.max(...area_distribution.map(a => a.count)) : 1;

  return (
    <div data-testid="supplier-analytics-page">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Supplier Analytics</h1>
        <p className="text-xs text-muted-foreground mt-0.5">Performance metrics and insights across your supplier base</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="analytics-summary">
        <StatCard icon={Users} label="Total Suppliers" value={summary.total_suppliers} accent="stat-accent-blue" />
        <StatCard icon={FileText} label="Documents" value={summary.total_documents} color="blue" accent="stat-accent-sky" />
        <StatCard icon={Star} label="Total Ratings" value={summary.total_ratings} color="amber" accent="stat-accent-amber" />
        <StatCard icon={ShieldCheck} label="Compliance Rate" value={`${summary.compliance_rate}%`} color="emerald" accent="stat-accent-emerald" />
        <StatCard icon={TrendUp} label="Avg Rating" value={summary.avg_rating || "N/A"} color="amber" accent="stat-accent-purple" />
      </div>

      {/* Row 1: Sector Distribution + Suppliers by Area */}
      <div className="grid gap-4 lg:grid-cols-2 mb-4">
        {/* Sector Distribution */}
        <div className="border border-border rounded-lg p-5 bg-card" data-testid="sector-distribution">
          <div className="flex items-center gap-2 mb-4">
            <Buildings size={18} className="text-primary" weight="duotone" />
            <h2 className="text-base font-semibold font-[Chivo]">Sector Distribution</h2>
            <span className="text-[10px] text-muted-foreground ml-auto">{sector_distribution.length} sectors</span>
          </div>
          {sector_distribution.length === 0 ? (
            <EmptyBlock icon={Buildings} message="No sectors defined" />
          ) : (
            <div className="space-y-2">
              {sector_distribution.slice(0, 12).map((s, i) => (
                <div key={s.name} className="flex items-center gap-3">
                  <span className="text-xs w-32 truncate shrink-0" title={s.name}>{s.name}</span>
                  <div className="flex-1 h-6 rounded bg-muted/50 overflow-hidden">
                    <div className={`h-full rounded transition-all flex items-center justify-end pr-2 ${BAR_COLORS[i % BAR_COLORS.length]}`}
                      style={{ width: `${Math.max(8, (s.count / maxSectorCount) * 100)}%` }}>
                      <span className="text-[10px] font-bold text-white">{s.count}</span>
                    </div>
                  </div>
                </div>
              ))}
              {sector_distribution.length > 12 && (
                <p className="text-[10px] text-muted-foreground text-right mt-1">+{sector_distribution.length - 12} more sectors</p>
              )}
            </div>
          )}
        </div>

        {/* Suppliers by Area */}
        <div className="border border-border rounded-lg p-5 bg-card" data-testid="area-distribution">
          <div className="flex items-center gap-2 mb-4">
            <MapPin size={18} className="text-primary" weight="duotone" />
            <h2 className="text-base font-semibold font-[Chivo]">Suppliers by Area</h2>
            <span className="text-[10px] text-muted-foreground ml-auto">{(area_distribution || []).length} areas</span>
          </div>
          {!area_distribution?.length ? (
            <EmptyBlock icon={MapPin} message="No area data available" />
          ) : (
            <div className="space-y-2">
              {area_distribution.slice(0, 12).map((a, i) => (
                <div key={a.name} className="flex items-center gap-3">
                  <span className="text-xs w-32 truncate shrink-0" title={a.name}>{a.name}</span>
                  <div className="flex-1 h-6 rounded bg-muted/50 overflow-hidden">
                    <div className={`h-full rounded transition-all flex items-center justify-end pr-2 ${BAR_COLORS[(i + 3) % BAR_COLORS.length]}`}
                      style={{ width: `${Math.max(8, (a.count / maxAreaCount) * 100)}%` }}>
                      <span className="text-[10px] font-bold text-white">{a.count}</span>
                    </div>
                  </div>
                </div>
              ))}
              {area_distribution.length > 12 && (
                <p className="text-[10px] text-muted-foreground text-right mt-1">+{area_distribution.length - 12} more areas</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Top Rated Suppliers + Status Distribution */}
      <div className="grid gap-4 lg:grid-cols-2 mb-4">
        {/* Top Rated Suppliers */}
        <div className="border border-border rounded-lg p-5 bg-card" data-testid="top-rated">
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={18} className="text-amber-500" weight="duotone" />
            <h2 className="text-base font-semibold font-[Chivo]">Top Rated Suppliers</h2>
          </div>
          {top_rated.length === 0 ? (
            <EmptyBlock icon={Trophy} message="No rated suppliers yet" />
          ) : (
            <div className="space-y-2">
              {top_rated.map((s, i) => (
                <div key={s.supplier_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/60 transition-colors cursor-pointer group"
                  onClick={() => navigate(`/vendors/${s.supplier_id}`)}
                  data-testid={`top-rated-${s.supplier_id}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      i === 0 ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"
                      : i === 1 ? "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                      : i === 2 ? "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400"
                      : "bg-muted text-muted-foreground"
                    }`}>
                      {i + 1}
                    </span>
                    <div>
                      <p className="text-xs font-semibold">{s.company_name}</p>
                      <p className="text-[10px] text-muted-foreground">{s.rating_count} rating{s.rating_count !== 1 ? "s" : ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Star size={14} weight="fill" className="text-amber-400" />
                    <span className="text-sm font-bold">{s.average_rating}</span>
                    <ArrowRight size={12} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Status Distribution */}
        <div className="border border-border rounded-lg p-5 bg-card" data-testid="status-distribution">
          <div className="flex items-center gap-2 mb-4">
            <ChartBar size={18} className="text-primary" weight="duotone" />
            <h2 className="text-base font-semibold font-[Chivo]">Status Distribution</h2>
          </div>
          {Object.entries(status_distribution).length === 0 ? (
            <EmptyBlock icon={ChartBar} message="No data" />
          ) : (
            <div className="space-y-4">
              {Object.entries(status_distribution).map(([status, count]) => {
                const pct = summary.total_suppliers > 0 ? Math.round((count / summary.total_suppliers) * 100) : 0;
                const cfg = {
                  active: { color: "bg-emerald-500", icon: "text-emerald-600 dark:text-emerald-400" },
                  inactive: { color: "bg-slate-400", icon: "text-slate-500" },
                  suspended: { color: "bg-red-500", icon: "text-red-500" },
                };
                const c = cfg[status] || cfg.active;
                return (
                  <div key={status}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className={`h-2.5 w-2.5 rounded-full ${c.color}`} />
                        <span className="text-sm font-medium capitalize">{status}</span>
                      </div>
                      <span className="text-sm font-bold">{count} <span className="text-xs font-normal text-muted-foreground">({pct}%)</span></span>
                    </div>
                    <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${c.color}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Rating Breakdown + Compliance Health */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Rating Breakdown */}
        <div className="border border-border rounded-lg p-5 bg-card" data-testid="rating-breakdown">
          <div className="flex items-center gap-2 mb-4">
            <Star size={18} className="text-amber-400" weight="fill" />
            <h2 className="text-base font-semibold font-[Chivo]">Rating Breakdown</h2>
          </div>
          {summary.total_ratings === 0 ? (
            <EmptyBlock icon={Star} message="No ratings yet" />
          ) : (
            <div className="space-y-3">
              {["quality", "reliability", "communication", "pricing", "overall"].map(key => {
                const val = rating_breakdown[key] || 0;
                const pct = (val / 5) * 100;
                return (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm capitalize">{key}</span>
                      <div className="flex items-center gap-1">
                        <Star size={12} weight="fill" className="text-amber-400" />
                        <span className="text-sm font-bold text-amber-600 dark:text-amber-400">{val}</span>
                      </div>
                    </div>
                    <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-amber-400 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Compliance Health */}
        <div className="border border-border rounded-lg p-5 bg-card" data-testid="compliance-health">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={18} className="text-emerald-500" weight="duotone" />
            <h2 className="text-base font-semibold font-[Chivo]">Compliance Health</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="text-center p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
              <p className="text-2xl font-bold font-[Chivo] text-emerald-600">{summary.compliance_rate}%</p>
              <p className="text-[10px] text-emerald-700 dark:text-emerald-400 mt-1">Compliance Rate</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <p className="text-2xl font-bold font-[Chivo] text-blue-600">{summary.total_documents}</p>
              <p className="text-[10px] text-blue-700 dark:text-blue-400 mt-1">Total Documents</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-muted-foreground" />
                <span className="text-xs">Total Suppliers</span>
              </div>
              <span className="text-sm font-bold">{summary.total_suppliers}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
              <div className="flex items-center gap-2">
                <Star size={16} className="text-amber-500" weight="fill" />
                <span className="text-xs">Average Rating</span>
              </div>
              <span className="text-sm font-bold">{summary.avg_rating || "N/A"}/5</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => navigate("/supplier-compliance")}>
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} className="text-emerald-500" />
                <span className="text-xs">View Full Compliance Report</span>
              </div>
              <ArrowRight size={14} className="text-muted-foreground" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = "primary", accent = "" }) {
  const colors = {
    primary: "text-primary",
    blue: "text-blue-600 dark:text-blue-400",
    amber: "text-amber-600 dark:text-amber-400",
    emerald: "text-emerald-600 dark:text-emerald-400",
  };
  return (
    <div className={`border border-border rounded-lg p-4 bg-card card-lift ${accent}`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon size={18} className={`${colors[color]} mb-2`} weight="duotone" />
      <p className="text-xl font-bold font-[Chivo]">{value}</p>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  );
}

function EmptyBlock({ icon: Icon, message }) {
  return (
    <div className="text-center py-10">
      <Icon size={24} className="mx-auto text-muted-foreground/40 mb-2" />
      <p className="text-xs text-muted-foreground">{message}</p>
    </div>
  );
}
